
// src/FrontSearchService.js
// Full front-only search using Google Maps JavaScript Places API (no backend).

function getApiKeyFromScriptTag() {
  try {
    const scripts = document.getElementsByTagName("script");
    for (const s of scripts) {
      const src = s.getAttribute("src") || "";
      if (src.includes("maps.googleapis.com/maps/api/js")) {
        const url = new URL(src, window.location.origin);
        const k = url.searchParams.get("key");
        if (k) return k;
      }
    }
  } catch {}
  return (typeof window !== "undefined" && window.GOOGLE_MAPS_API_KEY) || "";
}

export function photoUrlFromReference(photoReference, maxWidth = 800) {
  const key = getApiKeyFromScriptTag();
  if (!photoReference || !key) return null;
  return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=${maxWidth}&photoreference=${encodeURIComponent(photoReference)}&key=${encodeURIComponent(key)}`;
}

function computeMidpoint(locations) {
  const n = locations.length;
  if (!n) return null;
  const sum = locations.reduce(
    (acc, p) => ({ lat: acc.lat + Number(p.lat || 0), lng: acc.lng + Number(p.lng || 0) }),
    { lat: 0, lng: 0 }
  );
  return { lat: sum.lat / n, lng: sum.lng / n };
}

function haversine(a, b) {
  const R = 6371e3;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const s1 = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(s1), Math.sqrt(1 - s1));
  return R * c;
}

function nearbySearchAsync(service, request) {
  return new Promise((resolve, reject) => {
    service.nearbySearch(request, (results, status, pagination) => {
      const OK = (window.google?.maps?.places?.PlacesServiceStatus?.OK) || "OK";
      const ZERO = (window.google?.maps?.places?.PlacesServiceStatus?.ZERO_RESULTS) || "ZERO_RESULTS";
      if (status === OK || status === "OK") {
        resolve({ results: results || [], pagination });
      } else if (status === ZERO) {
        resolve({ results: [], pagination: null });
      } else {
        reject(new Error("Places nearbySearch failed: " + status));
      }
    });
  });
}

// NEW: get all pages (up to ~60 results total)
function nearbySearchAll(service, request) {
  return new Promise((resolve) => {
    let all = [];
    const handle = (results, status, pagination) => {
      const OK = (window.google?.maps?.places?.PlacesServiceStatus?.OK) || "OK";
      const ZERO = (window.google?.maps?.places?.PlacesServiceStatus?.ZERO_RESULTS) || "ZERO_RESULTS";
      if (status === OK || status === "OK") {
        all = all.concat(results || []);
        if (pagination && pagination.hasNextPage) {
          // Google impose un petit délai avant nextPage()
          setTimeout(() => pagination.nextPage(), 1200);
        } else {
          resolve(all);
        }
      } else if (status === ZERO) {
        resolve(all);
      } else {
        // on renvoie tout de même ce qu'on a
        resolve(all);
      }
    };
    service.nearbySearch(request, handle);
  });
}

async function reverseGeocodeMidpoint(mid) {
  try {
    const geocoder = new window.google.maps.Geocoder();
    const { results } = await geocoder.geocode({ location: mid });
    return (results && results[0]?.formatted_address) || `${mid.lat.toFixed(5)}, ${mid.lng.toFixed(5)}`;
  } catch {
    return `${mid.lat.toFixed(5)}, ${mid.lng.toFixed(5)}`;
  }
}

export async function findPlacesAtMidpoint({
  locations = [],
  radius = 5000,
  searchType = "restaurant",
  restaurantType = null,
  minRating = null,
  priceLevel = null,
  openNow = false,
}) {
  if (!window.google || !window.google.maps || !window.google.maps.places) {
    throw new Error("Google Maps Places library is not loaded. Vérifiez <script ... libraries=places,geometry>.");
  }
  if (!Array.isArray(locations) || locations.length < 2) {
    throw new Error("Veuillez fournir au moins 2 localisations valides.");
  }

  const midpoint = computeMidpoint(locations);
  const map = new window.google.maps.Map(document.createElement("div"), { center: midpoint, zoom: 13 });
  const service = new window.google.maps.places.PlacesService(map);

  const type = searchType === "hotel" ? "lodging" : "restaurant";

  const request = {
    location: midpoint,
    radius: Math.max(100, Math.min(50000, Number(radius) || 5000)),
    type,
    openNow: !!openNow,
    keyword: restaurantType || undefined,
  };

  // FETCH ALL PAGES INSTEAD OF ONLY FIRST
  let all = [];
  try {
    all = await nearbySearchAll(service, request);
  } catch (e) {
    try {
      // fallback (first page only) si quelque chose bloque all-pages
      const page1 = await nearbySearchAsync(service, request);
      all = page1.results || [];
    } catch {}
  }
  const fetched = all.length;

  // dedup by place_id
  const seen = new Set();
  const unique = [];
  for (const p of all) {
    if (p?.place_id && !seen.has(p.place_id)) {
      seen.add(p.place_id);
      unique.push(p);
    }
  }

  // filter client
  const filtered = unique.filter((p) => {
    if (minRating != null && (p.rating || 0) < minRating) return false;
    if (priceLevel != null && p.price_level != null && p.price_level !== priceLevel) return false;
    return true;
  });

  // normalize
  const norm = filtered.map((p) => {
    const loc = p.geometry?.location;
    const lat = loc ? (typeof loc.lat === "function" ? loc.lat() : loc.lat) : null;
    const lng = loc ? (typeof loc.lng === "function" ? loc.lng() : loc.lng) : null;
    return {
      place_id: p.place_id,
      name: p.name,
      address: p.vicinity || p.formatted_address || "",
      rating: p.rating ?? null,
      price_level: p.price_level ?? null,
      opening_hours: p.opening_hours || null,
      google_maps_url: p.url || (lat && lng ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.name)}&query_place_id=${p.place_id}` : null),
      website: null,
      phone_number: null,
      reservation_url: null,
      photo_url: Array.isArray(p.photos) && p.photos.length ? p.photos[0].getUrl({ maxWidth: 800 }) : null,
      geometry: p.geometry || null,
      distance_from_midpoint: (lat != null && lng != null) ? haversine({ lat: midpoint.lat, lng: midpoint.lng }, { lat, lng }) : null,
    };
  });

  const address = await reverseGeocodeMidpoint(midpoint);

  return {
    midpoint: { ...midpoint, address },
    places: norm,
    total_count: norm.length,
    meta: { fetched },
  };
}
